// Dimensionnement CVC d'un dossier (onglet de la conception) : zones et locaux, température extérieure de base sourcée,
// bilans froid et chaud par zone et par groupe, alertes, hypothèses visibles et modifiables, note de calcul PDF et HTML.
// Le calcul affiché est fait ICI avec le code partagé (shared/cvc/projet.ts) ; à chaque enregistrement automatique le
// serveur refait le même calcul, et c'est le sien qui fait foi (note de calcul). Rien d'inventé : un champ vide reste vide.
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, FileDown, Plus, Printer, RefreshCw, Trash2 } from 'lucide-react';
import * as P from '@shared/cvc/projet.ts';
import { ADAPTATIONS_CVC, ORIGINE_MOTEURS_CVC, VERSION_MOTEURS_CVC } from '@shared/cvc/origine.ts';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { dateHeure } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, EtatErreur, EtatVide, IndicateurEnregistrement, Panneau,
  useConfirmation, useNotifier, type EtatEnregistrement,
} from '../../components/ui.tsx';
import '../../styles/cvc.css';

const F = P.FROID;
const C = P.CHAUD;

// ─── Contrat de l'API (server/modules/cvc/routes.ts) ───
interface VueCvc {
  saisie: P.ProjetCvc | null; resultats: (P.CalculCvc['resultats'] & { bloquants: number }) | null; alertes: P.Alerte[];
  version: number | null; majLe: string | null; majPar: string | null; moteurVersion: string | null; moteurVersionCourante: string;
  droits: { ecrire: boolean };
}

const opts = (liste: readonly { id: string; nom: string }[]) => liste.map((x) => ({ valeur: x.id, libelle: x.nom }));
const fr = (x: number | null | undefined, d = 0) => (x == null || !Number.isFinite(x) ? '—' : (Math.round(x * 10 ** d) / 10 ** d).toLocaleString('fr-FR', { maximumFractionDigits: d }));
const kw = (w: number | null | undefined) => (w == null ? '—' : `${fr(w / 1000, 2)} kW`);
const copie = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

// ─── Saisie d'un nombre : virgule acceptée, bornes contrôlées, vide = « non renseigné » (jamais zéro) ───
function ChampNombre({ libelle, valeur, bornes, unite, aide, changer, desactive, entier, dec = 3, indication }: {
  libelle: string; valeur: number | null | undefined; bornes: readonly [number, number]; unite?: string; aide?: ReactNode;
  changer: (v: number | null) => void; desactive?: boolean; entier?: boolean; dec?: number; indication?: string;
}) {
  const afficher = (v: number | null | undefined) => (v == null ? '' : String(v).replace('.', ','));
  const [texte, setTexte] = useState(afficher(valeur));
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    const lu = P.lireNombre(texte, { min: bornes[0], max: bornes[1], entier, dec });
    if (lu !== undefined && lu !== (valeur ?? null)) { setTexte(afficher(valeur)); setErreur(null); }
  }, [valeur]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Champ libelle={unite ? `${libelle} (${unite})` : libelle} inputMode={entier ? 'numeric' : 'decimal'} value={texte} disabled={desactive}
      placeholder={indication} erreur={erreur} aide={aide} autoComplete="off"
      onChange={(e) => {
        const t = e.target.value;
        setTexte(t);
        const v = P.lireNombre(t, { min: bornes[0], max: bornes[1], entier, dec });
        if (v === undefined) { setErreur(`Valeur attendue entre ${fr(bornes[0], 3)} et ${fr(bornes[1], 3)}${entier ? ' (nombre entier)' : ''}.`); return; }
        setErreur(null);
        changer(v);
      }} />
  );
}

function Case({ libelle, coche, changer, desactive, aide }: { libelle: ReactNode; coche: boolean; changer: (v: boolean) => void; desactive?: boolean; aide?: ReactNode }) {
  const id = useId();
  return (
    <div className="pile" style={{ gap: 2 }}>
      <label className="case" htmlFor={id}>
        <input id={id} type="checkbox" checked={coche} disabled={desactive} onChange={(e) => changer(e.target.checked)} />
        <span>{libelle}</span>
      </label>
      {aide && <span className="champ-aide">{aide}</span>}
    </div>
  );
}

export function OngletDimensionnement({ dossierId }: { dossierId: string }) {
  const q = useQuery({ queryKey: ['cvc', dossierId], queryFn: () => api.get<VueCvc>(`/api/dossiers/${dossierId}/cvc`), staleTime: Infinity });
  const [demarre, setDemarre] = useState(false);
  if (q.isLoading) return <Chargement lignes={6} libelle="Chargement du dimensionnement…" />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const vue = q.data;
  if (!vue.saisie && !demarre) {
    return (
      <EtatVide icone={<Calculator />} titre="Aucun dimensionnement CVC pour ce dossier"
        texte={vue.droits.ecrire
          ? 'Saisissez les zones et les locaux (surface, hauteur, occupants, vitrages…), le département et l’altitude du chantier : les bilans froid et chaud sont calculés au fur et à mesure, avec leurs hypothèses.'
          : 'Personne n’a encore commencé le dimensionnement de ce dossier. Votre rôle permet de le consulter, pas de le saisir.'}
        action={vue.droits.ecrire ? <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setDemarre(true)}>Commencer le dimensionnement</Bouton> : undefined} />
    );
  }
  return <Editeur dossierId={dossierId} vue={vue} />;
}

function Editeur({ dossierId, vue }: { dossierId: string; vue: VueCvc }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const ecrire = vue.droits.ecrire;
  const [etat, setEtat] = useState<P.ProjetCvc>(() => vue.saisie ?? P.nouvelEtat());
  const [statut, setStatut] = useState<EtatEnregistrement>(vue.version ? 'enregistre' : 'modifie');
  const [quand, setQuand] = useState<string | undefined>(vue.majLe ? dateHeure(vue.majLe) : undefined);
  const [serveur, setServeur] = useState<VueCvc>(vue);
  const [conflit, setConflit] = useState(false);
  const version = useRef<number | null>(vue.version);
  const dernier = useRef(etat);
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined);
  const enCours = useRef(false);
  const aRefaire = useRef(false);
  const calcul = useMemo(() => P.calculer(etat), [etat]);

  const enregistrer = useCallback(async () => {
    clearTimeout(minuterie.current);
    if (enCours.current) { aRefaire.current = true; return; }
    enCours.current = true;
    const envoye = dernier.current;
    setStatut('en_cours');
    try {
      const r = await api.put<VueCvc>(`/api/dossiers/${dossierId}/cvc`, { saisie: envoye, version: version.current });
      version.current = r.version;
      setServeur(r);
      qc.setQueryData(['cvc', dossierId], r);
      setQuand(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      setStatut(dernier.current === envoye ? 'enregistre' : 'modifie');
    } catch (e) {
      setStatut('erreur');
      if (e instanceof ErreurApi && e.status === 409) setConflit(true);
      else {
        notifier(messageErreur(e), true);
        // Nouvelle tentative (réseau, serveur indisponible) : la saisie reste à l'écran entre-temps.
        minuterie.current = setTimeout(() => { void enregistrer(); }, 8000);
      }
    } finally {
      enCours.current = false;
      if (aRefaire.current) { aRefaire.current = false; void enregistrer(); }
    }
  }, [dossierId, notifier, qc]);

  // Premier enregistrement d'un dimensionnement neuf ; à la sortie de l'écran, rien en attente n'est perdu.
  useEffect(() => { if (ecrire && !vue.version) void enregistrer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (minuterie.current) { clearTimeout(minuterie.current); if (ecrire && !conflit) void enregistrer(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const maj = (f: (e: P.ProjetCvc) => P.ProjetCvc) => {
    if (!ecrire || conflit) return;
    const n = f(dernier.current);
    dernier.current = n;
    setEtat(n);
    setStatut('modifie');
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => { void enregistrer(); }, 800);
  };
  const majChamp = <K extends keyof P.ProjetCvc>(cle: K, valeur: Partial<P.ProjetCvc[K]>) =>
    maj((e) => { const n = copie(e); n[cle] = { ...(n[cle] as object), ...(valeur as object) } as P.ProjetCvc[K]; return n; });
  const majPiece = (id: string, champs: Partial<P.PieceProjet>) =>
    maj((e) => { const n = copie(e); n.pieces = n.pieces.map((p) => (p.id === id ? { ...p, ...champs } : p)); return n; });
  const majZone = (id: string, champs: Partial<P.ProjetCvc['zones'][number]>) =>
    maj((e) => { const n = copie(e); n.zones = n.zones.map((z) => (z.id === id ? { ...z, ...champs } : z)); return n; });

  const desactive = !ecrire || conflit;
  const r = calcul.resultats;
  const avecChaud = P.OBJECTIFS.find((o) => o.id === etat.objectif)?.chaud ?? true;
  const bloquants = calcul.bloquants.length;
  const aVerifier = calcul.alertes.length - bloquants;
  // Le serveur a-t-il trouvé le même résultat que l'écran ? (même code, même saisie : il doit.)
  const accord = statut === 'enregistre' && serveur.resultats
    && serveur.resultats.total_froid_w === r.total_froid_w && serveur.resultats.dimensionnement_chaud_w === r.dimensionnement_chaud_w
    && serveur.resultats.dimensionnement_froid_groupes_w === r.dimensionnement_froid_groupes_w;
  const noteDispo = version.current != null;
  const base = `/api/dossiers/${dossierId}/cvc`;

  const zonesOptions = etat.zones.map((z) => ({ valeur: z.id, libelle: z.nom }));
  const groupeDeZone = (zid: string) => etat.groupes.find((g) => g.zones.includes(zid))?.id ?? '';

  return (
    <div className="pile cvc-page">
      {confirmation}
      {!ecrire && <Alerte ton="info" titre="Lecture seule">Votre rôle permet de consulter ce dimensionnement et sa note de calcul, pas de le modifier.</Alerte>}
      {conflit && (
        <Alerte ton="danger" titre="Modifié ailleurs entre-temps"
          action={<Bouton icone={<RefreshCw aria-hidden />} onClick={() => qc.invalidateQueries({ queryKey: ['cvc', dossierId] }).then(() => window.location.reload())}>Recharger</Bouton>}>
          Une autre personne (ou un autre onglet) a enregistré ce dimensionnement. Rechargez-le avant de continuer : vos dernières modifications ne sont pas enregistrées.
        </Alerte>
      )}

      {/* ─── Bilan et actions ─── */}
      <section className="pile" aria-label="Bilan du dimensionnement">
        <div className="cvc-actions">
          {noteDispo
            ? <>
              <a className="btn btn-principal" href={`${base}/note.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />Note de calcul (PDF)</a>
              <a className="btn" href={`${base}/note.html`} target="_blank" rel="noopener"><Printer aria-hidden />Version imprimable</a>
            </>
            : <span className="petit secondaire">La note de calcul sera disponible après le premier enregistrement.</span>}
          {accord && <Badge ton="succes" titre="Le serveur a refait le calcul et trouve les mêmes valeurs ; c’est son résultat qui fait foi dans la note.">Calcul vérifié par le serveur</Badge>}
          {ecrire && <IndicateurEnregistrement etat={statut} quand={quand} />}
        </div>
        <div className="cvc-bilan">
          <Tuile libelle="Froid — terminaux" valeur={kw(r.dimensionnement_froid_terminaux_w)} note={r.total_froid_w == null ? 'à compléter' : `charge ${fr(r.total_froid_w)} W + ${fr(etat.securite.froid_pct, 1)} %`} />
          <Tuile libelle="Froid — groupes extérieurs" valeur={kw(r.dimensionnement_froid_groupes_w)} note={etat.simultaneite.s < 1 ? `simultanéité ${fr(etat.simultaneite.s, 2)}` : 'sans simultanéité déclarée'} />
          <Tuile libelle="Chaud dimensionné" valeur={avecChaud ? kw(r.dimensionnement_chaud_w) : 'sans objet'} note={avecChaud ? (r.phi_chaud_w == null ? 'non calculé' : `déperditions ${fr(r.phi_chaud_w)} W`) : 'rafraîchissement seul'} />
          <Tuile libelle="Température de base" valeur={r.theta_e_c == null ? 'non déterminée' : `${fr(r.theta_e_c, 1)} °C`}
            note={r.theta_e_c == null ? (avecChaud ? 'département et altitude à renseigner' : 'sans objet') : r.zone_climatique ? `zone ${r.zone_climatique}, table officielle` : 'valeur forcée sourcée'} />
          <Tuile libelle="Alertes" valeur={`${bloquants} bloquante${bloquants > 1 ? 's' : ''}`} note={`${aVerifier} à vérifier`} />
        </div>
      </section>

      {/* ─── Chantier et conditions ─── */}
      <Panneau titre="Chantier et conditions de calcul">
        <div className="pile">
          <div className="cvc-grille">
            <ChampChoix libelle="Objectif" value={etat.objectif} disabled={desactive} options={opts(P.OBJECTIFS)}
              onChange={(e) => maj((x) => ({ ...copie(x), objectif: e.target.value }))} />
            <ChampChoix libelle="Département du chantier" value={etat.batiment.departement} disabled={desactive} vide="Choisir…"
              options={P.LISTE_DEPARTEMENTS.map((d) => ({ valeur: d.id, libelle: d.nom }))}
              onChange={(e) => majChamp('batiment', { departement: e.target.value })} />
            <ChampNombre libelle="Altitude du chantier" unite="m" valeur={etat.batiment.altitude_m} bornes={P.BORNES.altitude} entier desactive={desactive}
              changer={(v) => majChamp('batiment', { altitude_m: v })} />
            <ChampChoix libelle="Ponts thermiques" value={etat.batiment.ponts} disabled={desactive} options={C.PONTS_THERMIQUES.map((p) => ({ valeur: p.id, libelle: p.nom }))}
              aide={`${fr(C.PONTS_THERMIQUES.find((p) => p.id === etat.batiment.ponts)?.valeur ?? null, 2)} W/m².K rapportés aux surfaces déperditives`}
              onChange={(e) => majChamp('batiment', { ponts: e.target.value })} />
          </div>
          {avecChaud && (r.theta_e_c != null
            ? <Alerte ton="info" titre={`Température extérieure de base retenue : ${fr(r.theta_e_c, 1)} °C${r.zone_climatique ? ` (zone ${r.zone_climatique})` : ''}`}>
              Source : {r.theta_e_source}
            </Alerte>
            : <Alerte ton="ambre" titre="Calcul chaud refusé">
              {calcul.alertes.find((a) => a.code === 'F14' || a.code === 'F06')?.message ?? 'Renseignez le département et l’altitude du chantier.'}
            </Alerte>)}
          {avecChaud && (
            <details className="cvc-local">
              <summary><span className="cvc-local-nom">Forcer la température de base (avec sa source)</span>
                <span className="cvc-local-resume">{etat.conditions.theta_e_force == null ? 'non : table officielle' : `forcée à ${fr(etat.conditions.theta_e_force, 1)} °C`}</span></summary>
              <div className="cvc-local-corps">
                <p className="petit secondaire">Laissez vide pour la table officielle. Une valeur forcée sans source est refusée (F06).</p>
                <div className="cvc-grille">
                  <ChampNombre libelle="Température de base forcée" unite="°C" valeur={etat.conditions.theta_e_force} bornes={[-40, 20]} dec={1} desactive={desactive}
                    changer={(v) => majChamp('conditions', { theta_e_force: v })} />
                  <div className="cvc-grille-large">
                    <ChampTexte libelle="Source de la valeur forcée" rows={2} value={etat.conditions.theta_e_source} disabled={desactive} maxLength={400}
                      onChange={(e) => majChamp('conditions', { theta_e_source: e.target.value })} />
                  </div>
                </div>
              </div>
            </details>
          )}
          <h3 className="cvc-sous-titre">Conditions d’été</h3>
          <div className="cvc-grille">
            <ChampNombre libelle="Extérieur" unite="°C" valeur={etat.conditions.ext_t} bornes={P.BORNES.temperature} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('conditions', { ext_t: v })} />
            <ChampNombre libelle="Humidité extérieure" unite="% HR" valeur={etat.conditions.ext_valeur} bornes={P.BORNES.humidite} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('conditions', { ext_valeur: v })} />
            <ChampNombre libelle="Intérieur" unite="°C" valeur={etat.conditions.int_t} bornes={P.BORNES.temperature} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('conditions', { int_t: v })} />
            <ChampNombre libelle="Humidité intérieure" unite="% HR" valeur={etat.conditions.int_valeur} bornes={P.BORNES.humidite} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('conditions', { int_valeur: v })} />
          </div>
          <Case libelle="Conditions d’été vérifiées pour ce site" coche={!!etat.conditions.verifiee} desactive={desactive}
            aide={etat.conditions.verifiee ? undefined : F.CONDITIONS_DEFAUT.source} changer={(v) => majChamp('conditions', { verifiee: v })} />
          <h3 className="cvc-sous-titre">Coefficients de sécurité et simultanéité</h3>
          <div className="cvc-grille">
            <ChampNombre libelle="Sécurité en froid" unite="%" valeur={etat.securite.froid_pct} bornes={P.BORNES.securite} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('securite', { froid_pct: v })} />
            <ChampNombre libelle="Sécurité en chaud" unite="%" valeur={etat.securite.chaud_pct} bornes={P.BORNES.securite} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('securite', { chaud_pct: v })} />
            <ChampNombre libelle="Simultanéité des groupes" valeur={etat.simultaneite.s} bornes={P.BORNES.simultaneite} dec={2} desactive={desactive}
              aide="De 0,5 à 1 ; ne s’applique qu’aux groupes extérieurs, jamais aux terminaux ni au chaud." changer={(v) => v != null && majChamp('simultaneite', { s: v })} />
            {etat.simultaneite.s < 1 && (
              <div className="cvc-grille-large">
                <ChampTexte libelle="Justification de la simultanéité (obligatoire)" rows={2} value={etat.simultaneite.justification} disabled={desactive} maxLength={400}
                  erreur={etat.simultaneite.justification.trim() ? null : 'Sans justification, le calcul des groupes est bloqué (F08).'}
                  onChange={(e) => majChamp('simultaneite', { justification: e.target.value })} />
              </div>
            )}
          </div>
        </div>
      </Panneau>

      {/* ─── Zones et locaux ─── */}
      <Panneau titre="Zones et locaux" actions={ecrire ? <Bouton icone={<Plus aria-hidden />} disabled={desactive || etat.pieces.length >= P.MAX.pieces}
        onClick={() => maj((e) => P.ajouterPiece(e))}>Ajouter une zone</Bouton> : undefined}>
        <div className="pile">
          <p className="petit secondaire">Une zone regroupe les locaux desservis par un même terminal ; un groupe extérieur alimente une ou plusieurs zones.
            Plusieurs locaux identiques se saisissent une fois avec leur nombre.</p>
          {etat.zones.map((z) => {
            const rz = r.zones.find((x) => x.id === z.id);
            const locaux = etat.pieces.filter((p) => p.zone_id === z.id);
            return (
              <section key={z.id} className="cvc-zone" aria-label={`Zone ${z.nom}`}>
                <div className="cvc-zone-entete">
                  <Champ libelle="Nom de la zone" value={z.nom} disabled={desactive} maxLength={80} onChange={(e) => majZone(z.id, { nom: e.target.value })} />
                  <ChampChoix libelle="Terminal" value={z.terminal} disabled={desactive} options={opts(P.TERMINAUX_ZONE)} onChange={(e) => majZone(z.id, { terminal: e.target.value })} />
                  <ChampChoix libelle="Groupe extérieur" value={groupeDeZone(z.id)} disabled={desactive} vide="Aucun"
                    options={etat.groupes.map((g) => ({ valeur: g.id, libelle: g.nom }))} onChange={(e) => maj((x) => P.rattacherZone(x, z.id, e.target.value))} />
                </div>
                <div className="cvc-zone-resultat" aria-live="polite">
                  {rz ? <>
                    <span>Froid <strong>{kw(rz.dimensionnement_froid_w)}</strong> ({fr(rz.total_w)} W, créneau {rz.creneau_nom.toLowerCase()})</span>
                    {avecChaud && <span>Chaud <strong>{kw(rz.dimensionnement_chaud_w)}</strong></span>}
                    {rz.air_neuf_cta_w ? <span>Air neuf à la CTA <strong>{kw(rz.air_neuf_cta_w)}</strong></span> : null}
                    {rz.incomplet && <Badge ton="ambre">Zone incomplète</Badge>}
                  </> : <span>Non calculée : locaux à compléter.</span>}
                </div>
                <div className="cvc-zone-corps">
                  {locaux.map((p) => (
                    <Local key={p.id} piece={p} calcul={calcul} desactive={desactive} zonesOptions={zonesOptions} avecChaud={avecChaud}
                      seul={etat.pieces.length <= 1} majPiece={majPiece} maj={maj}
                      supprimer={async () => { if (await confirmer('Supprimer ce local ?', `« ${p.nom} » et ses vitrages seront retirés du dimensionnement.`, 'Supprimer le local')) maj((e) => P.supprimerPiece(e, p.id)); }} />
                  ))}
                  {ecrire && (
                    <div><Bouton icone={<Plus aria-hidden />} disabled={desactive || etat.pieces.length >= P.MAX.pieces} onClick={() => maj((e) => P.ajouterPiece(e, z.id))}>Ajouter un local dans cette zone</Bouton></div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </Panneau>

      {/* ─── Groupes extérieurs ─── */}
      <Panneau titre="Groupes extérieurs" actions={ecrire ? <Bouton icone={<Plus aria-hidden />} disabled={desactive || etat.groupes.length >= P.MAX.groupes}
        onClick={() => maj((e) => P.ajouterGroupe(e))}>Ajouter un groupe</Bouton> : undefined}>
        <div className="pile">
          {etat.groupes.map((g) => {
            const rg = r.groupes.find((x) => x.id === g.id);
            return (
              <div key={g.id} className="rangee" style={{ alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <Champ libelle="Nom du groupe" value={g.nom} disabled={desactive} maxLength={80}
                    onChange={(e) => maj((x) => { const n = copie(x); n.groupes = n.groupes.map((y) => (y.id === g.id ? { ...y, nom: e.target.value } : y)); return n; })} />
                </div>
                <span className="petit" style={{ flex: '2 1 240px', paddingBottom: 10 }}>
                  {rg ? <>{g.zones.length} zone{g.zones.length > 1 ? 's' : ''} — terminaux {kw(rg.dimensionnement_terminaux_w)} → groupe <strong>{kw(rg.dimensionnement_froid_w)}</strong>{avecChaud ? <> ; chaud {kw(rg.dimensionnement_chaud_w)}</> : null}</>
                    : 'Aucune zone calculée rattachée.'}
                </span>
                {ecrire && etat.groupes.length > 1 && (
                  <Bouton variante="fantome" icone={<Trash2 aria-hidden />} disabled={desactive} aria-label={`Supprimer le groupe ${g.nom}`}
                    onClick={() => maj((e) => P.supprimerGroupe(e, g.id))}>Supprimer</Bouton>
                )}
              </div>
            );
          })}
        </div>
      </Panneau>

      {/* ─── Émetteurs à eau ─── */}
      {avecChaud && (
        <Panneau titre="Émetteurs à eau existants (chauffage basse température)">
          <div className="pile">
            <p className="petit secondaire">Pour une pompe à chaleur sur des radiateurs existants : la puissance nominale de chaque local (catalogue, régime d’essai)
              est ramenée au régime d’eau prévu par la loi des émetteurs. {C.SOURCE_EXPOSANT_EMETTEUR}</p>
            <div className="cvc-grille">
              <ChampNombre libelle="Température de départ" unite="°C" valeur={etat.emetteurs.depart_c} bornes={P.BORNES.eau} dec={1} desactive={desactive} changer={(v) => majChamp('emetteurs', { depart_c: v })} />
              <ChampNombre libelle="Température de retour" unite="°C" valeur={etat.emetteurs.retour_c} bornes={P.BORNES.eau} dec={1} desactive={desactive} changer={(v) => majChamp('emetteurs', { retour_c: v })} />
              <ChampNombre libelle="Exposant n" valeur={etat.emetteurs.exposant} bornes={P.BORNES.exposant} dec={2} desactive={desactive} changer={(v) => v != null && majChamp('emetteurs', { exposant: v })} />
              <ChampChoix libelle="Écart de température moyen" value={etat.emetteurs.moyenne} disabled={desactive}
                options={[{ valeur: 'arithmetique', libelle: 'Arithmétique (catalogues)' }, { valeur: 'logarithmique', libelle: 'Logarithmique' }]}
                onChange={(e) => majChamp('emetteurs', { moyenne: e.target.value as 'arithmetique' | 'logarithmique' })} />
              <ChampNombre libelle="Régime nominal : départ" unite="°C" valeur={etat.emetteurs.nominal_depart_c} bornes={P.BORNES.eau} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('emetteurs', { nominal_depart_c: v })} />
              <ChampNombre libelle="Régime nominal : retour" unite="°C" valeur={etat.emetteurs.nominal_retour_c} bornes={P.BORNES.eau} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('emetteurs', { nominal_retour_c: v })} />
              <ChampNombre libelle="Régime nominal : ambiance" unite="°C" valeur={etat.emetteurs.nominal_ambiance_c} bornes={[5, 35]} dec={1} desactive={desactive} changer={(v) => v != null && majChamp('emetteurs', { nominal_ambiance_c: v })} />
            </div>
            {calcul.emetteurs.length ? (
              <div className="tableau-conteneur">
                <table className="tableau">
                  <caption className="visuellement-cache">Émetteurs à eau par local</caption>
                  <thead><tr><th>Local</th><th className="num">Nominal</th><th className="num">Au régime</th><th className="num">Déperditions</th><th className="num">Couverture</th></tr></thead>
                  <tbody>{calcul.emetteurs.map((x) => (
                    <tr key={x.id}><td>{x.nom}</td><td className="num">{fr(x.nominal_w)} W</td><td className="num">{x.puissance_w == null ? '—' : `${fr(x.puissance_w)} W`}</td>
                      <td className="num">{x.deperditions_w == null ? '—' : `${fr(x.deperditions_w)} W`}</td>
                      <td className="num">{x.couverture == null ? '—' : <Badge ton={x.couverture >= 1 ? 'succes' : 'ambre'}>{fr(x.couverture * 100)} %</Badge>}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <p className="petit secondaire">Aucun émetteur saisi : renseignez la puissance nominale des émetteurs dans les locaux concernés.</p>}
          </div>
        </Panneau>
      )}

      {/* ─── Résultats ─── */}
      <Panneau titre="Résultats par zone et par groupe" serre>
        {r.zones.length ? (
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Résultats par zone</caption>
              <thead><tr><th>Zone</th><th className="num">Surface</th><th className="num">Sensible</th><th className="num">Latent</th><th className="num">Total froid</th>
                <th>Créneau</th><th className="num">Froid dimensionné</th>{avecChaud && <th className="num">Chaud dimensionné</th>}</tr></thead>
              <tbody>
                {r.zones.map((z) => (
                  <tr key={z.id}><td>{z.nom}</td><td className="num">{fr(z.surface_m2, 1)} m²</td><td className="num">{fr(z.sensible_w)} W</td><td className="num">{fr(z.latent_w)} W</td>
                    <td className="num">{fr(z.total_w)} W</td><td>{z.creneau_nom}</td><td className="num">{kw(z.dimensionnement_froid_w)}</td>
                    {avecChaud && <td className="num">{kw(z.dimensionnement_chaud_w)}</td>}</tr>
                ))}
                {r.groupes.map((g) => (
                  <tr key={g.id}><td><strong>{g.nom}</strong> (groupe)</td><td className="num">—</td><td className="num">—</td><td className="num">—</td>
                    <td className="num">{kw(g.dimensionnement_terminaux_w)}</td><td>s = {fr(g.simultaneite, 2)}</td><td className="num"><strong>{kw(g.dimensionnement_froid_w)}</strong></td>
                    {avecChaud && <td className="num"><strong>{kw(g.dimensionnement_chaud_w)}</strong></td>}</tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="panneau-corps"><p className="secondaire">Aucune zone calculable : complétez la surface et la hauteur d’au moins un local.</p></div>}
      </Panneau>

      {/* ─── Alertes ─── */}
      <Panneau titre={`Alertes (${calcul.alertes.length})`}>
        {calcul.alertes.length ? (
          <ul className="cvc-alertes">
            {calcul.alertes.map((a, i) => (
              <li key={i}><Badge ton={a.niveau === 'bloquant' ? 'danger' : 'ambre'}>{a.niveau === 'bloquant' ? 'Bloquant' : 'À vérifier'} · {a.code}</Badge><span>{a.message}</span></li>
            ))}
          </ul>
        ) : <p className="secondaire">Aucune alerte.</p>}
      </Panneau>

      {/* ─── Hypothèses et réglages ─── */}
      <Panneau titre="Hypothèses retenues et réglages du calcul">
        <div className="pile">
          <ul className="cvc-liste petit">{calcul.hypotheses.map((h, i) => <li key={i}>{h}</li>)}</ul>
          <h3 className="cvc-sous-titre">Réglages (hypothèses logiciel modifiables)</h3>
          <div>
            {P.REGLAGES_INFO.map((x) => (
              <div key={x.cle} className="cvc-reglage">
                <div><div>{x.libelle}</div><div className="cvc-source">{x.source}</div></div>
                <ChampNombre libelle="Valeur" unite={x.unite} valeur={etat.reglages[x.cle] as number} bornes={[0, 100000]} dec={4} desactive={desactive}
                  changer={(v) => v != null && majChamp('reglages', { [x.cle]: v } as Partial<P.Reglages>)} />
              </div>
            ))}
          </div>
          <Case libelle="Hypothèses vérifiées pour ce dossier" coche={etat.reglages.confirmees} desactive={desactive}
            aide="Tant que la case n’est pas cochée, la note de calcul indique « valeurs par défaut du logiciel, non vérifiées »."
            changer={(v) => majChamp('reglages', { confirmees: v })} />
          <details className="cvc-local">
            <summary><span className="cvc-local-nom">Origine des moteurs de calcul et adaptations à valider</span></summary>
            <div className="cvc-local-corps petit">
              <p>Copie versionnée de {ORIGINE_MOTEURS_CVC.depot}, commit <span className="mono">{ORIGINE_MOTEURS_CVC.commit.slice(0, 7)}</span> du {ORIGINE_MOTEURS_CVC.date},
                copiée le {ORIGINE_MOTEURS_CVC.copie} (version <span className="mono">{VERSION_MOTEURS_CVC}</span>).</p>
              <ul className="cvc-liste">{ORIGINE_MOTEURS_CVC.fichiers.map((f) => <li key={f.fichier}><span className="mono">{f.fichier}</span> — {f.reprise}</li>)}</ul>
              <ul className="cvc-liste">{ADAPTATIONS_CVC.map((a) => <li key={a.code}><strong>{a.code}</strong> (à valider) — {a.texte}</li>)}</ul>
              {serveur.moteurVersion && serveur.moteurVersion !== serveur.moteurVersionCourante && (
                <Alerte ton="ambre">Dernier enregistrement calculé avec la version {serveur.moteurVersion} ; la note est recalculée avec la version actuelle.</Alerte>
              )}
            </div>
          </details>
          <ul className="cvc-liste petit secondaire">{P.MENTIONS.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      </Panneau>
    </div>
  );
}

function Tuile({ libelle, valeur, note }: { libelle: string; valeur: string; note?: string }) {
  return (
    <div className="cvc-tuile">
      <div className="cvc-tuile-libelle">{libelle}</div>
      <div className="cvc-tuile-valeur">{valeur}</div>
      {note && <div className="cvc-tuile-note">{note}</div>}
    </div>
  );
}

function Local({ piece: p, calcul, desactive, zonesOptions, avecChaud, seul, majPiece, maj, supprimer }: {
  piece: P.PieceProjet; calcul: P.CalculCvc; desactive: boolean; zonesOptions: { valeur: string; libelle: string }[]; avecChaud: boolean; seul: boolean;
  majPiece: (id: string, champs: Partial<P.PieceProjet>) => void; maj: (f: (e: P.ProjetCvc) => P.ProjetCvc) => void; supprimer: () => void;
}) {
  const f = calcul.froids.find((x) => x.id === p.id);
  const ch = calcul.chauds.find((x) => x.id === p.id);
  const complet = f && F.estComplet(f) ? f : null;
  const type = F.TYPES_PIECE.find((t) => t.id === p.type);
  const defautS = type?.atelier ? calcul.etat.reglages.occupant_sensible_atelier_w : type?.occupation === 'assis' ? calcul.etat.reglages.occupant_sensible_assis_w : calcul.etat.reglages.occupant_sensible_w;
  const defautL = type?.atelier ? calcul.etat.reglages.occupant_latent_atelier_w : type?.occupation === 'assis' ? calcul.etat.reglages.occupant_latent_assis_w : calcul.etat.reglages.occupant_latent_w;
  const m = (champs: Partial<P.PieceProjet>) => majPiece(p.id, champs);
  // Ouvert au départ s'il reste à compléter ; ensuite c'est l'utilisateur qui l'ouvre ou le ferme (pas de repli pendant la saisie).
  const [ouvertAuDepart] = useState(() => !complet);
  const majVitrage = (vid: string, champs: Partial<NonNullable<P.PieceProjet['vitrages']>[number]>) =>
    maj((e) => { const n = copie(e); const x = n.pieces.find((y) => y.id === p.id)!; x.vitrages = (x.vitrages ?? []).map((v) => (v.id === vid ? { ...v, ...champs } : v)); return n; });
  const resume = complet
    ? `${type?.nom ?? ''} · ${fr(complet.surface_m2, 1)} m²${(p.count ?? 1) > 1 ? ` × ${p.count}` : ''} · froid ${fr(complet.total_w)} W${avecChaud && ch?.phi_w != null ? ` · chaud ${fr(ch.phi_w)} W` : ''}`
    : `${type?.nom ?? ''} · à compléter`;
  return (
    <details className="cvc-local" open={ouvertAuDepart || undefined}>
      <summary><span className="cvc-local-nom">{p.nom}</span><span className="cvc-local-resume">{resume}</span>
        {f && !complet && <Badge ton="ambre">Données manquantes</Badge>}</summary>
      <div className="cvc-local-corps">
        <div className="cvc-grille">
          <Champ libelle="Nom du local" value={p.nom} disabled={desactive} maxLength={200} onChange={(e) => m({ nom: e.target.value })} />
          <ChampChoix libelle="Type de local" value={p.type} disabled={desactive} options={opts(F.TYPES_PIECE)} onChange={(e) => m({ type: e.target.value })} />
          <ChampNombre libelle="Nombre de locaux identiques" valeur={p.count} bornes={P.BORNES.count} entier desactive={desactive} changer={(v) => m({ count: v ?? 1 })} />
          <ChampChoix libelle="Zone" value={p.zone_id ?? ''} disabled={desactive} options={zonesOptions} onChange={(e) => maj((x) => P.deplacerPiece(x, p.id, e.target.value))} />
        </div>

        <h4 className="cvc-sous-titre">Géométrie et enveloppe</h4>
        <div className="cvc-grille">
          <ChampNombre libelle="Surface" unite="m²" valeur={p.surface_m2} bornes={P.BORNES.surface} dec={2} desactive={desactive} changer={(v) => m({ surface_m2: v })} />
          <ChampNombre libelle="Hauteur sous plafond" unite="m" valeur={p.hauteur_m} bornes={P.BORNES.hauteur} dec={2} desactive={desactive} changer={(v) => v != null && m({ hauteur_m: v })}
            aide={(p.hauteur_m ?? 0) > 4 ? 'Au-delà de 4 m, la stratification n’est pas calculée (alerte F04).' : undefined} />
          <ChampNombre libelle="Façades extérieures" valeur={p.facades} bornes={P.BORNES.facades} entier desactive={desactive} indication="2 supposées si vide" changer={(v) => m({ facades: v })} />
          <ChampChoix libelle="Position" value={p.position} disabled={desactive} options={opts(F.POSITIONS)} onChange={(e) => m({ position: e.target.value })} />
          <ChampChoix libelle="Plancher bas" value={p.plancher_bas} disabled={desactive} options={opts(C.PLANCHERS_BAS)} onChange={(e) => m({ plancher_bas: e.target.value })} />
          <ChampChoix libelle="Isolation" value={p.isolation} disabled={desactive} options={opts(F.ISOLATIONS)} onChange={(e) => m({ isolation: e.target.value })} />
          <ChampChoix libelle="Vitrage par défaut" value={p.vitrage_defaut} disabled={desactive} options={opts(F.VITRAGES)} onChange={(e) => m({ vitrage_defaut: e.target.value })} />
          <ChampNombre libelle="Surface des portes" unite="m²" valeur={p.portes_m2} bornes={[0, 200]} dec={2} desactive={desactive} changer={(v) => m({ portes_m2: v })} />
        </div>

        <h4 className="cvc-sous-titre">Vitrages et orientations</h4>
        {(p.vitrages ?? []).map((v, i) => (
          <div key={v.id} className="cvc-vitrage" aria-label={`Vitrage ${i + 1}`}>
            <ChampChoix libelle="Orientation" value={v.orientation} disabled={desactive} options={F.ORIENTATIONS.map((o) => ({ valeur: o, libelle: o }))} onChange={(e) => majVitrage(v.id!, { orientation: e.target.value })} />
            <ChampNombre libelle="Largeur" unite="m" valeur={v.largeur_m} bornes={P.BORNES.cote} dec={2} desactive={desactive} changer={(x) => majVitrage(v.id!, { largeur_m: x })} />
            <ChampNombre libelle="Hauteur" unite="m" valeur={v.hauteur_m} bornes={P.BORNES.cote} dec={2} desactive={desactive} changer={(x) => majVitrage(v.id!, { hauteur_m: x })} />
            <ChampNombre libelle="Nombre" valeur={v.nombre} bornes={[1, 200]} entier desactive={desactive} changer={(x) => majVitrage(v.id!, { nombre: x ?? 1 })} />
            <ChampChoix libelle="Vitrage" value={v.vitrage} disabled={desactive} options={opts(F.VITRAGES)} onChange={(e) => majVitrage(v.id!, { vitrage: e.target.value })} />
            <ChampChoix libelle="Protection" value={v.protection} disabled={desactive} options={opts(F.PROTECTIONS)} onChange={(e) => majVitrage(v.id!, { protection: e.target.value })} />
            {!desactive && <Bouton variante="fantome" icone={<Trash2 aria-hidden />} aria-label={`Retirer le vitrage ${i + 1}`} onClick={() => maj((e) => P.supprimerVitrage(e, p.id, v.id!))}>Retirer</Bouton>}
          </div>
        ))}
        {!desactive && (p.vitrages ?? []).length < P.MAX.vitrages && (
          <div><Bouton icone={<Plus aria-hidden />} onClick={() => maj((e) => P.ajouterVitrage(e, p.id))}>Ajouter un vitrage</Bouton></div>
        )}

        <h4 className="cvc-sous-titre">Occupation et apports internes</h4>
        <div className="cvc-grille">
          <ChampNombre libelle="Occupants" valeur={p.occupants} bornes={P.BORNES.personnes} entier desactive={desactive} changer={(v) => m({ occupants: v })} />
          <ChampNombre libelle="Apport sensible par occupant" unite="W" valeur={p.occupant_sensible_w} bornes={P.BORNES.apport_occupant} dec={1} desactive={desactive}
            indication={`${fr(defautS)} par défaut`} changer={(v) => m({ occupant_sensible_w: v })} />
          <ChampNombre libelle="Apport latent par occupant" unite="W" valeur={p.occupant_latent_w} bornes={P.BORNES.apport_occupant} dec={1} desactive={desactive}
            indication={`${fr(defautL)} par défaut`} changer={(v) => m({ occupant_latent_w: v })} />
          <ChampNombre libelle="Éclairage" unite="W" valeur={p.eclairage_w} bornes={P.BORNES.puissance} entier desactive={desactive} changer={(v) => m({ eclairage_w: v })} />
          <ChampNombre libelle="Équipements (sensible)" unite="W" valeur={p.equipements_w} bornes={P.BORNES.puissance} entier desactive={desactive} changer={(v) => m({ equipements_w: v })} />
          <ChampNombre libelle="Postes informatiques" valeur={p.postes_info} bornes={[0, 1000]} entier desactive={desactive} changer={(v) => m({ postes_info: v })} />
        </div>
        {complet && <p className="cvc-source">Apports par occupant retenus : {fr(complet.apports_occupant.sensible_w)} W + {fr(complet.apports_occupant.latent_w)} W — {complet.apports_occupant.source}</p>}

        <h4 className="cvc-sous-titre">Air neuf et infiltrations</h4>
        <div className="cvc-grille">
          <ChampNombre libelle="Débit d’air neuf" unite="m³/h" valeur={p.ventilation_m3h} bornes={P.BORNES.debit} entier desactive={desactive} changer={(v) => m({ ventilation_m3h: v })} />
          <ChampNombre libelle="Infiltrations" unite="vol/h" valeur={p.infiltration_volh} bornes={P.BORNES.infiltration} dec={2} desactive={desactive} indication="non comptées si vide" changer={(v) => m({ infiltration_volh: v })} />
          <ChampNombre libelle="Récupération de chaleur (hiver)" unite="%" valeur={p.recuperation_pct} bornes={P.BORNES.pourcentage} entier desactive={desactive} changer={(v) => m({ recuperation_pct: v })} />
        </div>
        <Case libelle="Air neuf prétraité par une CTA" coche={!!p.air_neuf_pretraite} desactive={desactive}
          aide="La ventilation du local arrive à l’état de soufflage ; la charge de l’air neuf jusqu’à cet état revient à la CTA." changer={(v) => m({ air_neuf_pretraite: v })} />
        {p.air_neuf_pretraite && (
          <div className="cvc-grille">
            <ChampNombre libelle="Température de soufflage" unite="°C" valeur={p.soufflage_t} bornes={P.BORNES.soufflage} dec={1} desactive={desactive} changer={(v) => m({ soufflage_t: v })}
              aide={p.soufflage_t == null ? 'Obligatoire : fiche de sélection de la CTA.' : undefined} />
            <ChampNombre libelle="Humidité de soufflage" unite="% HR" valeur={p.soufflage_hr} bornes={P.BORNES.humidite} entier desactive={desactive} changer={(v) => m({ soufflage_hr: v })}
              aide={p.soufflage_hr == null ? 'Obligatoire.' : undefined} />
          </div>
        )}
        {complet?.air_neuf_cta && <p className="cvc-source">Charge de l’air neuf à reprendre par la CTA : {fr(complet.air_neuf_cta.total_w)} W (hors besoin du local).</p>}

        {avecChaud && <>
          <h4 className="cvc-sous-titre">Chauffage</h4>
          <div className="cvc-grille">
            <ChampNombre libelle="Température intérieure de confort" unite="°C" valeur={p.theta_int_c} bornes={[5, 35]} dec={1} desactive={desactive}
              indication={`${type?.theta ?? 20} selon le type`} changer={(v) => m({ theta_int_c: v })} />
            <ChampNombre libelle="Émetteurs à eau existants : puissance nominale" unite="W" valeur={p.emetteurs_nominal_w} bornes={P.BORNES.puissance} entier desactive={desactive}
              changer={(v) => m({ emetteurs_nominal_w: v })} />
          </div>
          <Case libelle="Occupation intermittente (relance non calculée)" coche={!!p.intermittence} desactive={desactive} changer={(v) => m({ intermittence: v })} />
        </>}

        {complet && (
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Détail du bilan froid du local {p.nom}</caption>
              <thead><tr><th>Terme (un local)</th><th className="num">Puissance</th></tr></thead>
              <tbody>
                {[...complet.rangs_sensibles, ...complet.rangs_latents].map((x) => <tr key={x[0]}><td>{x[0]}</td><td className="num">{fr(x[1])} W</td></tr>)}
                <tr><td><strong>Total froid</strong></td><td className="num"><strong>{fr(complet.total_w)} W</strong></td></tr>
                {avecChaud && ch?.phi_w != null && <tr><td><strong>Déperditions (chaud)</strong></td><td className="num"><strong>{fr(ch.phi_w)} W</strong></td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {!desactive && !seul && <div><Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={supprimer}>Supprimer le local</Bouton></div>}
      </div>
    </details>
  );
}
